import { Component, Input, Output, EventEmitter, inject, ViewChild, ElementRef, AfterViewChecked, signal } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { LucideAngularModule, Loader2, Check, AlertCircle, CheckCheck, Eye, Code } from 'lucide-angular';
import { Message} from "../../../../core/models/message.model";
import { copyToClipboard } from "../../../../core/utils/utils";
import { WhatsAppFormatter } from "../../../../core/utils/whatsapp-formatter";
import { ChatEventService } from "../../../../core/services/chat-event.service";
import { TraceFormatterService } from "../../../../core/services/trace-formatter.service";
import { ArticleExpansionPanelComponent } from "../../../../shared/components/chat/article-expansion-panel/article-expansion-panel.component";
import { MessageButtonComponent } from "../../../../shared/components/chat/message-button/message-button.component";
import { DiscardedMessageInlineComponent } from "../../../../shared/components/chat/discarded-message-inline/discarded-message-inline.component";

@Component({
  selector: "app-new-message-list",
  standalone: true,
  imports: [
    CommonModule, 
    DatePipe, 
    MatButtonModule, 
    MatTooltipModule,
    MatCardModule,
    LucideAngularModule,
    ArticleExpansionPanelComponent,
    MessageButtonComponent,
    DiscardedMessageInlineComponent,
  ],
  template: `
    <div
      class="overflow-hidden">
      @for (
        message of messages || [];
        track message.id;
        let first = $first;
        let last = $last;
        let i = $index
        ) {
        <!-- Visualizador de Conversaciones: Mostrar mensajes descartados antes -->
        @if (isViewerMode) {
          @for (discarded of getDiscardedBefore(message); track discarded.id) {
            <app-discarded-message-inline [messages]="getDiscardedGroupMessages(discarded)"></app-discarded-message-inline>
          }
        }
    
        <div
          [class]="
            'flex ' +
            (message.isUser ? 'justify-end' : 'justify-start') +
            (first ? ' mt-5' :
              (!message.isUser && isConsecutiveBotMessage(i) ? ' mt-14' : ' mt-1'))
          "
          >
          @if (message.isUser) {
            <!-- User Message -->
            <div [class]="'flex items-center gap-3 max-w-[75%] group justify-end' + (last ? ' mb-5' : '')">
              <!-- Botones de acción del usuario (lado izquierdo) -->
              <div class="opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col gap-1">
                <div class="tooltip">
                  <button
                    mat-fab
                    class="message-action-btn !w-8 !h-8 !min-h-8 !shadow-md !bg-light-surface dark:!bg-dark-surface !text-light-text-secondary dark:!text-dark-text-secondary hover:!bg-light-bg-tertiary dark:hover:!bg-dark-bg-tertiary"
                    (click)="copyMessage(message.content)"
                    >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  </button>
                  <span class="tooltiptext user-tooltip">Copiar mensaje</span>
                </div>
              </div>
    
              <!-- Mensaje del usuario -->
              <div class="py-5">
                <div class="message-bubble message-user min-w-[150px]">
                  <div
                    class="text-sm leading-relaxed whitespace-pre-wrap whatsapp-formatted"
                    [innerHTML]="formatWhatsAppText(message.content)">
                  </div>
                  <div class="flex items-center justify-end gap-1.5 mt-3">
                    <span class="text-xs opacity-80 timestamp-hover">
                      <span class="time-short">{{ message.timestamp | date: "shortTime" }}</span>
                      <span class="time-full">{{ formatFullTime(message.timestamp) }}</span>
                    </span>
                    <!-- Sending status indicator -->
                    <span class="inline-flex items-center justify-center w-5 h-5 rounded-full shadow-sm">
                      @if (message.sendingStatus === 'sending') {
                        <lucide-angular [img]="Loader2" class="w-5 h-5 animate-spin text-white"></lucide-angular>
                      } @else if (message.sendingStatus === 'sent') {
                        <lucide-angular [img]="CheckCheck" class="w-5 h-5 text-white"></lucide-angular>
                      } @else if (message.sendingStatus === 'error') {
                        <lucide-angular [img]="AlertCircle" class="w-5 h-5 text-red-500 dark:text-red-200"></lucide-angular>
                      } @else {
                        <lucide-angular [img]="CheckCheck" class="w-5 h-5 text-white"></lucide-angular>
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
    
        <!-- Chat en vivo: mostrar acordeón de pensamiento DESPUÉS del mensaje del usuario -->
        @if (message.isUser && !isViewerMode && hasAnyTraces(message.id)) {
          <div class="flex justify-start">
            <div class="flex items-start gap-3 max-w-[85%] mb-3">
              <!-- Avatar del bot -->
              <div class="w-8 h-8 bg-gradient-to-br from-light-primary to-light-primary-variant dark:from-dark-primary dark:to-dark-primary-variant rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span class="text-white text-xs font-semibold">AI</span>
              </div>
    
              <div class="flex-1">
                <details>
                  <!-- Mostrar resumen del pensamiento del chatbot -->
                  <summary class="cursor-pointer px-3 py-2 text-s font-medium text-light-text-secondary dark:text-dark-text-secondary hover:bg-[#1A73E8]/10 dark:hover:bg-[#1A73E8]/10 transition-colors flex items-center gap-2 rounded-lg">
                    <svg class="w-4 h-4 transition-transform [[open]_&]:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                    <span class="thinking-text" [style.opacity]="getThinkingOpacity(message.id)">{{ getThinkingText(message.id) }}</span>
                  </summary>
                  <!-- Acordeón -->
                  <!-- Mostrar todas las trazas agrupadas por agente -->
                  <div #tracesContainer class="p-3 !pt-0 space-y-1 bg-white dark:bg-dark-bg max-h-[450px] min-h-0 overflow-y-auto">
                    @for (agentGroup of traceFormatter.groupTracesByAgent(getAllTraces(message.id)); track agentGroup.agentName) {
                      <!-- Encabezado del agente -->
                      <div class="text-base font-semibold pt-2">
                        {{ agentGroup.agentName }}
                      </div>
                      <div class="border-l-2 border-light-border dark:border-dark-border pl-3 ml-1">
                        <!-- Trazas del agente -->
                        <div class="space-y-3">
                          @for (processMsg of agentGroup.traces; track processMsg.id) {
                            @if (processMsg.type === 'articles') {
                              Recuperación de Artículos
                            } @else if (processMsg.traceType === 'thinking') {
                              <div class="border-l-2 rounded border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/10 px-2">
                                <div class="flex flex-row items-center justify-between">
                                  <div class="text-sm mt-1 font-medium text-purple-600 dark:text-purple-400 mb-1">Thinking</div>
                                  <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                </div>
                                <div class="content-box compact-scroll whitespace-pre-wrap text-sm text-black dark:text-white" [innerHTML]="formatWhatsAppText(processMsg.content)"></div>
                              </div>
                            } @else if (processMsg.traceType === 'tool_use') {
                              <div class="border-l-2 rounded border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10 px-2">
                                <div class="flex flex-row items-center justify-between">
                                  <div class="text-sm mt-1 font-medium text-green-600 dark:text-green-400 mb-1">
                                    Tool Use: <span class="font-semibold">{{ traceFormatter.getToolName(processMsg) }}</span>
                                  </div>
                                  <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                </div>
                                <!-- Parameters Section -->
                                <div class="text-sm mb-2">
                                  @if (traceFormatter.getToolParams(processMsg) && traceFormatter.getToolParams(processMsg).length > 0) {
                                    <div class="space-y-1">
                                      @for (param of traceFormatter.getToolParams(processMsg); track $index) {
                                        <div>
                                          <strong class="text-green-700 dark:text-green-300">{{param.name}}:</strong>
                                          <span class="text-black dark:text-white ml-1">{{param.value}}</span>
                                        </div>
                                      }
                                    </div>
                                  } @else {
                                    <div class="text-light-text-tertiary dark:text-dark-text-tertiary italic text-xs">No parameters available</div>
                                  }
                                </div>
                              </div>
                            } @else if (processMsg.traceType === 'tool_output') {
                              <div class="border-l-2 rounded border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/10 px-2">
                                <div class="flex flex-row items-center justify-between">
                                  <div class="text-sm mt-1 font-medium text-yellow-600 dark:text-yellow-400 mb-1">Tool Output</div>
                                  <div class="flex items-center gap-4">
                                    @if (traceFormatter.isKnowledgeBaseResponse(processMsg.content) || traceFormatter.isJsonOutput(processMsg.content)) {
                                      <button
                                        mat-icon-button
                                        [class]="'!w-6 !h-6 !p-0 ' + (getViewMode(processMsg) === 'clean' ? '!bg-yellow-100 dark:!bg-yellow-900/30' : '')"
                                        (click)="setViewMode(processMsg, 'clean')"
                                        [matTooltip]="traceFormatter.isKnowledgeBaseResponse(processMsg.content) ? 'Ver artículos' : 'Ver formato limpio'"
                                        >
                                        <lucide-icon
                                          [img]="EyeIcon"
                                          class="w-4 h-4 text-yellow-600 dark:text-yellow-400"
                                        ></lucide-icon>
                                      </button>
                                    }
                                    <button
                                      mat-icon-button
                                      [class]="'!w-6 !h-6 !p-0 ' + (getViewMode(processMsg) === 'raw' ? '!bg-yellow-100 dark:!bg-yellow-900/30' : '')"
                                      (click)="setViewMode(processMsg, 'raw')"
                                      matTooltip="Ver respuesta completa"
                                      >
                                      <lucide-icon
                                        [img]="CodeIcon"
                                        class="w-4 h-4 text-yellow-600 dark:text-yellow-400"
                                      ></lucide-icon>
                                    </button>
                                    <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                  </div>
                                </div>
                                @if (getViewMode(processMsg) === 'clean' && traceFormatter.isKnowledgeBaseResponse(processMsg.content)) {
                                  <div class="knowledge-articles-grid mb-2">
                                    @for (article of traceFormatter.parseKnowledgeBaseResponse(processMsg.content); track article.order) {
                                      <div class="kb-article-card">
                                        <div class="flex flex-row pt-1.5">
                                          <span class="text-sm font-semibold">#{{ article.order }}</span>
                                          <span class="text-sm font-semibold">{{ article.mainCategory }}</span>
                                        </div>
                                        <div class="pt-0.7">
                                          <span class="text-sm font-medium">Subcategoría: </span><span class="kb-article-subcategory text-sm font-normal">{{ article.subCategory }}</span>
                                        </div>
                                        <div class="pt-0.7">
                                          <span class="text-sm font-medium">Question: </span><span class="text-sm font-normal">{{ article.question }}</span>
                                        </div>
                                        <div class="pt-0.7">
                                          <span class="text-sm font-medium">Response: </span><span class="kb-article-response text-sm font-normal">{{ article.response }}</span>
                                        </div>
                                      </div>
                                    }
                                  </div>
                                } @else if (getViewMode(processMsg) === 'clean' && traceFormatter.isJsonOutput(processMsg.content)) {
                                  <div class="mb-2 max-w-full overflow-y-auto">
                                    <pre class="text-xs font-normal whitespace-pre-wrap break-words" [innerHTML]="traceFormatter.formatJsonOutput(processMsg.content)"></pre>
                                  </div>
                                } @else if (getViewMode(processMsg) === 'raw') {
                                  <div class="whitespace-pre-wrap italic p-2 rounded mb-1 text-xs" [innerHTML]="formatWhatsAppText(processMsg.content)"></div>
                                }
                              </div>
                            } @else if (processMsg.traceType === 'final_answer') {
                              <div class="border-l-2 rounded border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/10 px-2">
                                <div class="flex flex-row items-center justify-between">
                                  <div class="text-sm mt-1 font-medium text-blue-600 dark:text-blue-400 mb-1">Final Answer</div>
                                  <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                </div>
                                <div class="content-box compact-scroll whitespace-pre-wrap text-sm text-black dark:text-white" [innerHTML]="formatWhatsAppText(processMsg.content)"></div>
                              </div>
                            } @else if (processMsg.traceType === 'model_invocation_info') {
                              <div class="border-l-2 rounded border-light-border dark:border-dark-border bg-light-bg-secondary dark:bg-dark-bg-secondary px-2">
                                <div class="flex flex-row items-center justify-between">
                                  <div class="text-sm mt-1 font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Métricas del modelo</div>
                                  <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                </div>
                                <div class="content-box compact-scroll whitespace-pre-wrap text-sm text-black dark:text-white" [innerHTML]="formatWhatsAppText(processMsg.content)"></div>
                              </div>
                            } @else if (processMsg.traceType === 'summary') {
                              <div class="border-l-2 rounded border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/10 px-2">
                                <div class="flex flex-row items-center justify-between">
                                  <div class="text-sm mt-1 font-medium text-indigo-600 dark:text-indigo-400 mb-1">
                                    {{ processMsg.rawTrace?.body?.label || 'Summary' }}: <span class="font-semibold">{{ processMsg.rawTrace?.body?.tool_name || '' }}</span>
                                  </div>
                                  <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                </div>
                                @if (processMsg.rawTrace?.body?.description) {
                                  <div class="text-xs text-indigo-700 dark:text-indigo-300 mb-1">{{ processMsg.rawTrace.body.description }}</div>
                                }
                                @if (processMsg.rawTrace?.body?.fields?.length) {
                                  <div class="space-y-0.5 mb-1">
                                    @for (field of processMsg.rawTrace.body.fields; track field.name) {
                                      <div class="text-sm">
                                        <strong class="text-indigo-700 dark:text-indigo-300">{{ field.name }}:</strong>
                                        <span class="text-black dark:text-white ml-1">{{ field.value }}</span>
                                      </div>
                                    }
                                  </div>
                                }
                                @if (processMsg.rawTrace?.body?.summary) {
                                  <div class="text-sm mt-1 mb-1">
                                    <strong class="text-indigo-700 dark:text-indigo-300">Resumen:</strong>
                                    <span class="text-black dark:text-white ml-1 whitespace-pre-wrap">{{ processMsg.rawTrace.body.summary }}</span>
                                  </div>
                                }
                              </div>
                            } @else if (processMsg.traceType === 'info') {
                              <div class="border-l-2 rounded border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/10 px-2">
                                <div class="flex flex-row items-center justify-between">
                                  <div class="text-sm mt-1 font-medium text-gray-600 dark:text-gray-400 mb-1">Info</div>
                                  <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                </div>
                                <div class="content-box compact-scroll whitespace-pre-wrap text-xs text-black dark:text-white max-h-32 overflow-y-auto" [innerHTML]="formatWhatsAppText(processMsg.content)"></div>
                              </div>
                            }
                          }
                        </div>
                      </div>
                    }
                  </div>
                </details>
              </div>
            </div>
          </div>
        }
    
        <!-- Bot Message -->
        @if (!message.isUser) {
          <!-- Visualizador de Conversaciones: Acordeón de pensamiento ANTES del mensaje del bot -->
          @if (isViewerMode && hasBotTraces(message.id) && isFirstVisibleBotForAnswer(message.id)) {
            <div class="flex justify-start">
              <div class="flex items-start gap-3 max-w-[85%] mb-3">
                <!-- Avatar del bot -->
                <div class="w-8 h-8 bg-gradient-to-br from-light-primary to-light-primary-variant dark:from-dark-primary dark:to-dark-primary-variant rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span class="text-white text-xs font-semibold">AI</span>
                </div>
    
                <!-- Acordeón -->
                <div class="flex-1">
                  <details>
                    <summary class="cursor-pointer px-3 py-2 text-s font-medium text-light-text-secondary dark:text-dark-text-secondary hover:bg-[#1A73E8]/10 dark:hover:bg-[#1A73E8]/10 transition-colors flex items-center gap-2 rounded-lg">
                      <svg class="w-4 h-4 transition-transform [[open]_&]:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                      </svg>
                      <span class="thinking-text">Pensamiento</span>
                    </summary>
                    <!-- Si hay trazas, mostrar el acordeón -->
                    <div #tracesContainer class="p-3 !pt-0 space-y-1 bg-white dark:bg-dark-surface max-h-[450px] min-h-0 overflow-y-auto">
                      @for (agentGroup of traceFormatter.groupTracesByAgent(getBotTraces(message.id)); track agentGroup.agentName) {
                        <!-- Encabezado del agente -->
                        <div class="text-base font-semibold pt-2">
                          {{ agentGroup.agentName }}
                        </div>
                        <div class="border-l-2 border-light-border dark:border-dark-border pl-3 ml-1">
                          <!-- Trazas del agente -->
                          <div class="space-y-3">
                            @for (processMsg of agentGroup.traces; track processMsg.id) {
                              @if (processMsg.type === 'articles') {
                                Recuperación de Artículos
                              } @else if (processMsg.traceType === 'thinking') {
                                <div class="border-l-2 rounded border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/10 px-2">
                                  <div class="flex flex-row items-center justify-between">
                                    <div class="text-sm mt-1 font-medium text-purple-600 dark:text-purple-400 mb-1">Thinking</div>
                                    <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                  </div>
                                  <div class="content-box compact-scroll whitespace-pre-wrap text-sm text-black dark:text-white" [innerHTML]="formatWhatsAppText(processMsg.content)"></div>
                                </div>
                              } @else if (processMsg.traceType === 'tool_use') {
                                <div class="border-l-2 rounded border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10 px-2">
                                  <div class="flex flex-row items-center justify-between">
                                    <div class="text-sm mt-1 font-medium text-green-600 dark:text-green-400 mb-1">
                                      Tool Use: <span class="font-semibold">{{ traceFormatter.getToolName(processMsg) }}</span>
                                    </div>
                                    <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                  </div>
                                  <!-- Parameters Section -->
                                  <div class="text-sm mb-2">
                                    @if (traceFormatter.getToolParams(processMsg) && traceFormatter.getToolParams(processMsg).length > 0) {
                                      <div class="space-y-1">
                                        @for (param of traceFormatter.getToolParams(processMsg); track $index) {
                                          <div>
                                            <strong class="text-green-700 dark:text-green-300">{{param.name}}:</strong>
                                            <span class="text-black dark:text-white ml-1">{{param.value}}</span>
                                          </div>
                                        }
                                      </div>
                                    } @else {
                                      <div class="text-light-text-tertiary dark:text-dark-text-tertiary italic text-xs">No parameters available</div>
                                    }
                                  </div>
                                </div>
                              } @else if (processMsg.traceType === 'tool_output') {
                                <div class="border-l-2 rounded border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/10 px-2">
                                  <div class="flex flex-row items-center justify-between">
                                    <div class="text-sm mt-1 font-medium text-yellow-600 dark:text-yellow-400 mb-1">Tool Output</div>
                                    <div class="flex items-center gap-4">
                                      @if (traceFormatter.isKnowledgeBaseResponse(processMsg.content) || traceFormatter.isJsonOutput(processMsg.content)) {
                                        <button
                                          mat-icon-button
                                          [class]="'!w-6 !h-6 !p-0 ' + (getViewMode(processMsg) === 'clean' ? '!bg-yellow-100 dark:!bg-yellow-900/30' : '')"
                                          (click)="setViewMode(processMsg, 'clean')"
                                          [matTooltip]="traceFormatter.isKnowledgeBaseResponse(processMsg.content) ? 'Ver artículos' : 'Ver formato limpio'"
                                          >
                                          <lucide-icon
                                            [img]="EyeIcon"
                                            class="w-4 h-4 text-yellow-600 dark:text-yellow-400"
                                          ></lucide-icon>
                                        </button>
                                      }
                                      <button
                                        mat-icon-button
                                        [class]="'!w-6 !h-6 !p-0 ' + (getViewMode(processMsg) === 'raw' ? '!bg-yellow-100 dark:!bg-yellow-900/30' : '')"
                                        (click)="setViewMode(processMsg, 'raw')"
                                        matTooltip="Ver respuesta completa"
                                        >
                                        <lucide-icon
                                          [img]="CodeIcon"
                                          class="w-4 h-4 text-yellow-600 dark:text-yellow-400"
                                        ></lucide-icon>
                                      </button>
                                      <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                    </div>
                                  </div>
                                  @if (getViewMode(processMsg) === 'clean' && traceFormatter.isKnowledgeBaseResponse(processMsg.content)) {
                                    <div class="knowledge-articles-grid mb-2">
                                      @for (article of traceFormatter.parseKnowledgeBaseResponse(processMsg.content); track article.order) {
                                        <div class="kb-article-card">
                                          <div class="flex flex-row pt-1.5">
                                            <span class="text-sm font-semibold">#{{ article.order }}</span>
                                            <span class="text-sm font-semibold">{{ article.mainCategory }}</span>
                                          </div>
                                          <div class="pt-0.7">
                                            <span class="text-sm font-medium">Subcategoría: </span><span class="kb-article-subcategory text-sm font-normal">{{ article.subCategory }}</span>
                                          </div>
                                          <div class="pt-0.7">
                                            <span class="text-sm font-medium">Question: </span><span class="text-sm font-normal">{{ article.question }}</span>
                                          </div>
                                          <div class="pt-0.7">
                                            <span class="text-sm font-medium">Response: </span><span class="kb-article-response text-sm font-normal">{{ article.response }}</span>
                                          </div>
                                        </div>
                                      }
                                    </div>
                                  } @else if (getViewMode(processMsg) === 'clean' && traceFormatter.isJsonOutput(processMsg.content)) {
                                    <div class="mb-2 max-w-full overflow-y-auto">
                                      <pre class="text-xs font-normal whitespace-pre-wrap break-words" [innerHTML]="traceFormatter.formatJsonOutput(processMsg.content)"></pre>
                                    </div>
                                  } @else if (getViewMode(processMsg) === 'raw') {
                                    <div class="whitespace-pre-wrap italic p-2 rounded mb-1 text-xs" [innerHTML]="formatWhatsAppText(processMsg.content)"></div>
                                  }
                                </div>
                              } @else if (processMsg.traceType === 'final_answer') {
                                <div class="border-l-2 rounded border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/10 px-2">
                                  <div class="flex flex-row items-center justify-between">
                                    <div class="text-sm mt-1 font-medium text-blue-600 dark:text-blue-400 mb-1">Final Answer</div>
                                    <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                  </div>
                                  <div class="content-box compact-scroll whitespace-pre-wrap text-sm text-black dark:text-white" [innerHTML]="formatWhatsAppText(processMsg.content)"></div>
                                </div>
                              } @else if (processMsg.traceType === 'model_invocation_info') {
                                <div class="border-l-2 rounded border-light-border dark:border-dark-border bg-light-bg-secondary dark:bg-dark-bg-secondary px-2">
                                  <div class="flex flex-row items-center justify-between">
                                    <div class="text-sm mt-1 font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Métricas del modelo</div>
                                    <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                  </div>
                                  <div class="content-box compact-scroll whitespace-pre-wrap text-sm text-black dark:text-white" [innerHTML]="formatWhatsAppText(processMsg.content)"></div>
                                </div>
                              } @else if (processMsg.traceType === 'summary') {
                                <div class="border-l-2 rounded border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/10 px-2">
                                  <div class="flex flex-row items-center justify-between">
                                    <div class="text-sm mt-1 font-medium text-indigo-600 dark:text-indigo-400 mb-1">
                                      {{ processMsg.rawTrace?.body?.label || 'Summary' }}: <span class="font-semibold">{{ processMsg.rawTrace?.body?.tool_name || '' }}</span>
                                    </div>
                                    <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                  </div>
                                  @if (processMsg.rawTrace?.body?.description) {
                                    <div class="text-xs text-indigo-700 dark:text-indigo-300 mb-1">{{ processMsg.rawTrace.body.description }}</div>
                                  }
                                  @if (processMsg.rawTrace?.body?.fields?.length) {
                                    <div class="space-y-0.5 mb-1">
                                      @for (field of processMsg.rawTrace.body.fields; track field.name) {
                                        <div class="text-sm">
                                          <strong class="text-indigo-700 dark:text-indigo-300">{{ field.name }}:</strong>
                                          <span class="text-black dark:text-white ml-1">{{ field.value }}</span>
                                        </div>
                                      }
                                    </div>
                                  }
                                  @if (processMsg.rawTrace?.body?.summary) {
                                    <div class="text-sm mt-1 mb-1">
                                      <strong class="text-indigo-700 dark:text-indigo-300">Resumen:</strong>
                                      <span class="text-black dark:text-white ml-1 whitespace-pre-wrap">{{ processMsg.rawTrace.body.summary }}</span>
                                    </div>
                                  }
                                </div>
                              } @else if (processMsg.traceType === 'info') {
                                <div class="border-l-2 rounded border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/10 px-2">
                                  <div class="flex flex-row items-center justify-between">
                                    <div class="text-sm mt-1 font-medium text-gray-600 dark:text-gray-400 mb-1">Info</div>
                                    <div class="text-sm font-normal text-light-text-tertiary dark:text-dark-text-tertiary">{{ agentGroup.timestamp | date: 'HH:mm:ss' }}</div>
                                  </div>
                                  <div class="content-box compact-scroll whitespace-pre-wrap text-xs text-black dark:text-white max-h-32 overflow-y-auto" [innerHTML]="formatWhatsAppText(processMsg.content)"></div>
                                </div>
                              }
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </details>
                </div>
              </div>
            </div>
          }
          <div [class]="'flex justify-start' + (isConsecutiveBotMessage(i) && !(isViewerMode && hasBotTraces(message.id) && isFirstVisibleBotForAnswer(message.id)) ? ' -mt-10' : '')">
            <div [class]="'flex items-center gap-3 max-w-[85%] group relative' + (last ? ' mb-5' : '')">
              <!-- Espaciador fijo siempre presente -->
              <div class="w-8 flex-shrink-0"></div>
    
              <!-- Avatar posicionado absolutamente cuando sea necesario -->
              @if (shouldShowBotAvatar(message.id, i)) {
                <div class="absolute left-0 top-0 w-8 h-8 bg-gradient-to-br from-light-primary to-light-primary-variant dark:from-dark-primary dark:to-dark-primary-variant rounded-full flex items-center justify-center flex-shrink-0">
                  <span class="text-white text-xs font-semibold">AI</span>
                </div>
              }
    
              <!-- Mensaje del bot -->
              <div class="message-bubble message-bot">
                <div
                  class="text-sm leading-relaxed whitespace-pre-wrap whatsapp-formatted"
                  [innerHTML]="formatWhatsAppText(message.content)">
                </div>
    
                @if ((message.buttons && message.buttons.length > 0) || (message.selectList && message.selectList.options.length > 0)) {
                  <app-message-button
                    [message]="message"
                    (buttonClick)="onButtonClick($event)">
                  </app-message-button>
                }
    
                <div class="flex items-center justify-between mt-3">
                  <p class="small timestamp-hover">
                    <span class="time-short">{{ message.timestamp | date: "shortTime" }}</span>
                    <span class="time-full">{{ formatFullTime(message.timestamp) }}</span>
                  </p>
                </div>
              </div>
    
              <!-- Botones de acción del bot (lado derecho) -->
              <div class="opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col gap-1">
                <!-- Botón de artículos (solo si existen) -->
                @if (message.retrievedArticles && message.retrievedArticles.length > 0) {
                  <app-article-expansion-panel
                    #articleExpansionPanel
                    [retrievedArticles]="message.retrievedArticles"
                    [messages]="messages"
                    [messageId]="message.id"
                  ></app-article-expansion-panel>
                  <div class="tooltip">
                    <button
                      mat-fab
                      class="message-action-btn !w-8 !h-8 !min-h-8 !shadow-md !bg-light-surface dark:!bg-dark-surface !text-light-text-secondary dark:!text-dark-text-secondary hover:!bg-light-bg-tertiary dark:hover:!bg-dark-bg-tertiary"
                      (click)="articleExpansionPanel.openDialog()"
                      >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </button>
                    <span class="tooltiptext bot-tooltip">Artículos</span>
                  </div>
                }
    
                <!-- Botón de copiar -->
                <div class="tooltip">
                  <button
                    mat-fab
                    class="message-action-btn !w-8 !h-8 !min-h-8 !shadow-md !bg-light-surface dark:!bg-dark-surface !text-light-text-secondary dark:!text-dark-text-secondary hover:!bg-light-bg-tertiary dark:hover:!bg-dark-bg-tertiary"
                    (click)="copyMessage(message.content)"
                    >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  </button>
                  <span class="tooltiptext bot-tooltip">Copiar mensaje</span>
                </div>
              </div>
            </div>
          </div>
        }
      }
      @if (!messages || messages.length === 0) {
        <div class="text-center py-12">
          <div
            class="w-16 h-16 bg-light-bg-secondary dark:bg-dark-surface-variant rounded-2xl flex items-center justify-center mx-auto mb-4"
            >
            <svg
              class="w-8 h-8 text-light-text-tertiary dark:text-dark-text-tertiary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              ></path>
            </svg>
          </div>
          <p class="normal">
            Inicia la conversación enviando un mensaje.
          </p>
        </div>
      }
    </div>
    `,  
  styles: [`
    /* Tooltip container */
    .tooltip {
      position: relative;
      display: inline-block;
    }

    /* Base tooltip styles */
    .tooltip .tooltiptext {
      visibility: hidden;
      width: max-content;
      max-width: clamp(220px, 35vw, 300px);
      background-color: #f8fafc;
      color: #1f2937;
      text-align: left;
      padding: clamp(4px, 0.8vw, 6px) clamp(8px, 1.4vw, 10px);
      border-radius: 6px;
      font-size: clamp(9px, 1.5vw, 11px);
      line-height: 1.3;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      border: 1px solid rgba(0, 0, 0, 0.1);
      position: absolute;
      z-index: 9999;
      opacity: 0;
      transition: opacity 0.2s;
      pointer-events: none;
      white-space: nowrap;
      word-break: break-word;
    }

    /* Bot tooltips - always to the LEFT */
    .tooltip .tooltiptext.bot-tooltip {
      right: 100%;
      margin-right: clamp(4px, 1vw, 8px);
      top: 50%;
      transform: translateY(-50%);
      text-align: right;
      white-space: normal;

    }

    /* User tooltips - always to the RIGHT */
    .tooltip .tooltiptext.user-tooltip {
      left: 100%;
      margin-left: clamp(6px, 1vw, 8px);
      top: 50%;
      transform: translateY(-50%);
      text-align: left;

    }

    /* Show tooltip on hover */
    .tooltip:hover .tooltiptext {
      visibility: visible;
      opacity: 1;
    }

    /* Dark mode tooltips */
    :host-context(.dark) .tooltip .tooltiptext {
      background-color: #374151;
      color: #f9fafb;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    /* Compact content for multi-line tooltips */
    .tooltip .tooltiptext div {
      margin: 0;
      padding: 0;
      line-height: 1.2;
    }

    .tooltip .tooltiptext div:not(:last-child) {
      margin-bottom: clamp(1px, 0.3vw, 2px);
    }

    /* Button hover effects */
    .message-action-btn {
      transition: all 0.2s ease !important;
    }

    .message-action-btn:hover {
      transform: scale(1.05) !important;
    }

    /* Responsive adjustments */
    @media (max-width: 768px) {
      .tooltip .tooltiptext {
        font-size: clamp(11px, 2vw, 13px);
        padding: clamp(3px, 0.6vw, 4px) clamp(4px, 1vw, 6px);
      }
    }

    /* Timestamp hover functionality */
    .timestamp-hover {
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .timestamp-hover .time-full {
      display: none;
    }

    .timestamp-hover .time-short {
      display: inline;
    }

    .timestamp-hover:hover .time-short {
      display: none;
    }

    .timestamp-hover:hover .time-full {
      display: inline;
    }

    /* Thinking text transition */
    .thinking-text {
      transition: opacity 400ms ease-out;
      opacity: 1;
    }

    /* Animación de puntos pensando */
    .thinking-dots {
      display: inline-block;
    }

    .thinking-dots span {
      animation: thinking-blink 1.4s infinite;
      opacity: 0;
    }

    .thinking-dots span:nth-child(1) {
      animation-delay: 0s;
    }

    .thinking-dots span:nth-child(2) {
      animation-delay: 0.2s;
    }

    .thinking-dots span:nth-child(3) {
      animation-delay: 0.4s;
    }

    @keyframes thinking-blink {
      0%, 20% {
        opacity: 0;
      }
      40%, 60% {
        opacity: 1;
      }
      80%, 100% {
        opacity: 0;
      }
    }

  `],
})
export class MessageListComponent implements AfterViewChecked {
  public chatService = inject(ChatEventService);
  public traceFormatter = inject(TraceFormatterService);

  // Lucide icons
  readonly Loader2 = Loader2;
  readonly Check = Check;
  readonly AlertCircle = AlertCircle;
  readonly CheckCheck = CheckCheck;
  readonly EyeIcon = Eye;
  readonly CodeIcon = Code;
  
  // Track view mode for each trace: null = hidden, 'clean' = articles, 'raw' = complete
  private readonly traceViewModes = signal<Map<string, 'clean' | 'raw'>>(new Map());
  
  // Private field to store all messages (including discarded)
  private _allMessages: Message[] | null = [];
  
  // Viewer mode flag - when true, shows DISCARDED messages with special styling
  private _isViewerMode: boolean = false;
  
  @Input() 
  set isViewerMode(value: boolean) {
    this._isViewerMode = value;
    // Recompute visible messages when viewer mode changes
    this.recomputeVisibleMessages();
  }

  get isViewerMode(): boolean {
    return this._isViewerMode;
  }
  
  // Input setter that stores all messages and recomputes visible ones
  @Input() 
  set messages(value: Message[] | null) {
    this._allMessages = value;
    this.recomputeVisibleMessages();
  }
  
  // Getter that returns filtered messages
  get messages(): Message[] | null {
    return this._visibleMessages;
  }

  /**
   * Recompute _visibleMessages based on current _allMessages and _isViewerMode.
   * Called from both the messages setter and isViewerMode setter to handle any input order.
   */
  private recomputeVisibleMessages(): void {
    const value = this._allMessages;
    if (this._isViewerMode) {
      // In viewer mode, filter out DISCARDED messages and empty MERGED bot messages
      // MERGED messages with text content are shown; empty MERGED bot messages (trace-only) are hidden
      this._visibleMessages = value?.filter(msg => {
        if (msg.state === 'DISCARDED') return false;
        if (msg.state === 'MERGED' && !msg.isUser && !msg.content?.trim()) return false;
        return true;
      }) || [];
    } else {
      // In live chat mode, filter out DISCARDED messages
      this._visibleMessages = value?.filter(msg => msg.state !== 'DISCARDED') || [];
    }
  }
  
  private _visibleMessages: Message[] | null = [];
  
  @Output() sendMessage = new EventEmitter<string>();
  @Output() scrollToBottom = new EventEmitter<void>();

  @ViewChild("tracesContainer") tracesContainer!: ElementRef;

  readonly lastTraceCounts: Map<string, number> = new Map();

  // Estado para transiciones de texto thinking con cola
  readonly thinkingState: Map<string, { 
    displayText: string; 
    opacity: number; 
    queue: string[];
    isTransitioning: boolean;
    lastProcessedText: string;
  }> = new Map();


  ngAfterViewChecked(): void {
    // Autoscroll para cada contenedor de trazas abierto y notificar al padre
    if (!this.messages) return;
    let shouldScrollParent = false;
    this.messages.forEach((msg, idx) => {
      const traces = this.getUserMessageEvents(msg.id);
      const prevCount = this.lastTraceCounts.get(msg.id) ?? 0;
      if (traces.length > prevCount) {
        // Scroll local en el contenedor de trazas
        const container = this.tracesContainer.nativeElement;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
        // Marcar para notificar al padre
        shouldScrollParent = true;
      }
      this.lastTraceCounts.set(msg.id, traces.length);
    });
    if (shouldScrollParent) {
      this.scrollToBottom.emit();
    }
  }

  
  formatFullTime(date: Date | string): string {
    return new Date(date).toLocaleString('en-US', {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  }

  copyMessage(text: string): void {
    copyToClipboard(text);
  }

  formatWhatsAppText(text: string): string {
    return WhatsAppFormatter.format(text);
  }

  isLastUserMessage(userMessageId: string): boolean {
    if (!this.messages) return false;
    
    // Encontrar el último mensaje de usuario en la lista
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].isUser) {
        return this.messages[i].id === userMessageId;
      }
    }
    
    return false;
  }

  getUserMessageEvents(userMessageId: string) {
    return this.chatService.pendingProcessMessages().filter(pm => pm.answeredMessageId === userMessageId);
  }

  /**
   * Get stored agent traces from the bot message that answered this user message
   */
  getStoredAgentTraces(userMessageId: string): any[] {
    const botMessage = this.findBotMessageForUser(userMessageId);
    if (!botMessage?.agentTrace) return [];
    
    return this.parseAndTransformAgentTrace(botMessage);
  }

  private findBotMessageForUser(userMessageId: string): Message | undefined {
    if (!this._allMessages) return undefined;
    
    // In live mode, exclude DISCARDED messages
    // In viewer mode, include all messages
    const messages = this.isViewerMode 
      ? this._allMessages
      : this._allMessages.filter(m => m.state !== 'DISCARDED');
    
    return messages.find(m => !m.isUser && m.answeredMessageId === userMessageId);
  }

  private parseAndTransformAgentTrace(botMessage: Message): any[] {
    try {
      const normalized = this.traceFormatter.normalizeAgentTrace(botMessage.agentTrace, botMessage.agentName);
      if (!normalized) return [];
      const result = this.transformAgentTraceToDisplayFormat(normalized, botMessage.id);
      return result;
    } catch (error) {
      console.error('Error parsing agentTrace:', error);
      return [];
    }
  }

  private transformAgentTraceToDisplayFormat(agentTrace: any, botMessageId: string): any[] {
    if (!Array.isArray(agentTrace)) return [];
    
    const traces: any[] = [];
    agentTrace.forEach((agent: any) => {
      const agentTraces = this.extractTracesFromAgent(agent, botMessageId);
      traces.push(...agentTraces);
    });
    return traces;
  }

  private extractTracesFromAgent(agent: any, botMessageId: string): any[] {
    const agentName = agent.agent_name || agent.agentName || 'Unknown';
    const agentTraces = agent.traces;
    
    if (!agentTraces || !Array.isArray(agentTraces)) return [];
    
    return agentTraces.map((trace: any, index: number) => ({
      id: `${botMessageId}_${agentName}_${index}`,
      type: 'stored_trace',
      traceType: trace.type,
      content: this.formatTraceContent(trace),
      timestamp: this.parseTraceTimestamp(trace.timestamp),
      agentName: agentName,
      rawTrace: trace
    }));
  }

  /**
   * Parse trace timestamp safely. Handles:
   * - Epoch seconds strings like "1772650165.372452"
   * - Epoch seconds numbers
   * - ISO date strings
   * - Empty/null/undefined → null (no timestamp shown)
   */
  private parseTraceTimestamp(ts: any): Date | null {
    if (!ts) return null;
    
    // Already a valid Date
    if (ts instanceof Date) {
      return Number.isNaN(ts.getTime()) ? null : ts;
    }
    
    // Try as numeric epoch timestamp
    const num = typeof ts === 'number' ? ts : Number.parseFloat(ts);
    if (!Number.isNaN(num)) {
      // Epoch in seconds (< 10 billion) vs milliseconds
      return num < 10_000_000_000 ? new Date(num * 1000) : new Date(num);
    }
    
    // Try as date string
    const date = new Date(ts);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  /**
   * Format trace content for display
   */
  private formatTraceContent(trace: any): string {
    const formatters: Record<string, () => string> = {
      'thinking': () => this.formatThinkingTrace(trace),
      'tool_use': () => this.formatToolUseTrace(trace),
      'tool_output': () => this.formatToolOutputTrace(trace),
      'final_answer': () => this.formatFinalAnswerTrace(trace),
      'model_invocation_info': () => this.formatModelInvocationTrace(trace),
      'info': () => this.toDisplayString(trace.body?.text ?? this.formatDefaultTrace(trace)),
      'summary': () => this.formatSummaryTrace(trace)
    };

    const formatter = formatters[trace.type];
    return this.toDisplayString(formatter ? formatter() : this.formatDefaultTrace(trace));
  }

  private formatThinkingTrace(trace: any): string {
    return this.toDisplayString(trace.body?.text ?? this.formatDefaultTrace(trace));
  }

  private formatToolUseTrace(trace: any): string {
    if (!trace.body) return this.formatDefaultTrace(trace);
    const toolName = trace.body.tool_name || 'Unknown';
    const toolInput = this.extractToolInput(trace.body);
    return this.formatToolParams(toolName, toolInput);
  }

  private extractToolInput(traceBody: any): any {
    if (traceBody.tool_input) {
      return traceBody.tool_input;
    }
    
    if (traceBody.params && Array.isArray(traceBody.params)) {
      return traceBody.params.reduce((acc: any, param: any) => {
        if (param.name && param.value !== undefined) {
          acc[param.name] = param.value;
        }
        return acc;
      }, {});
    }
    
    return {};
  }

  private formatToolParams(toolName: string, toolInput: any): string {
    let result = `Tool: ${toolName}`;
    for (const [key, value] of Object.entries(toolInput)) {
      result += `\n  ${key}: ${value}`;
    }
    return result;
  }

  private formatToolOutputTrace(trace: any): string {
    return this.toDisplayString(trace.body?.text ?? this.formatDefaultTrace(trace));
  }

  private formatFinalAnswerTrace(trace: any): string {
    return this.toDisplayString(trace.body?.text ?? this.formatDefaultTrace(trace));
  }

  private formatModelInvocationTrace(trace: any): string {
    if (!trace.body) return this.formatDefaultTrace(trace);
    const usage = trace.body.usage || {};
    const modelId = trace.body.model_id || 'Unknown';
    const totalTokens = usage.total_tokens || 0;
    const inputTokens = usage.input_tokens || 0;
    const outputTokens = usage.output_tokens || 0;
    return `Model: ${modelId}\nTokens: ${totalTokens} (input: ${inputTokens}, output: ${outputTokens})`;
  }

  private formatSummaryTrace(trace: any): string {
    const body = trace.body || {};
    let result = body.tool_name ? `${body.label || 'Summary'}: ${body.tool_name}` : 'Summary';
    if (body.description) result += `\n${body.description}`;
    if (body.fields?.length) {
      for (const f of body.fields) result += `\n${f.name}: ${f.value}`;
    }
    if (body.summary) result += `\n\nResumen: ${body.summary}`;
    return result;
  }

  private formatDefaultTrace(trace: any): string {
    return JSON.stringify(trace.body || {}, null, 2);
  }

  private toDisplayString(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  /**
   * Get all traces (real-time + stored) for a user message
   * In viewer mode, excludes traces from DISCARDED bot messages
   */
  getAllTraces(userMessageId: string) {
    const realtimeTraces = this.getUserMessageEvents(userMessageId);
    const storedTraces = this.getStoredAgentTraces(userMessageId);
    
    // In viewer mode, exclude traces if the bot message is DISCARDED
    // (those traces will be shown inside the discarded message component)
    if (this.isViewerMode) {
      const botMessage = this.findBotMessageForUser(userMessageId);
      if (botMessage?.state === 'DISCARDED') {
        return []; // Don't show accordion for DISCARDED messages
      }
    }
    
    return [...realtimeTraces, ...storedTraces];
  }

  /**
   * Check if there are any traces (real-time or stored) for a user message
   * In viewer mode, returns false if the bot message is DISCARDED
   */
  hasAnyTraces(userMessageId: string): boolean {
    // In viewer mode, don't show accordion if bot message is DISCARDED
    if (this.isViewerMode) {
      const botMessage = this.findBotMessageForUser(userMessageId);
      if (botMessage?.state === 'DISCARDED') {
        return false; // Traces will be shown inside the discarded message component
      }
    }
    
    return this.getUserMessageEvents(userMessageId).length > 0 || 
           this.getStoredAgentTraces(userMessageId).length > 0;
  }

  /**
   * Check if a specific bot message has traces (including MERGED messages)
   * Used to determine if accordion should be shown for this bot message
   */
  hasBotTraces(botMessageId: string): boolean {
    if (!this._allMessages) return false;
    
    const botMessage = this._allMessages.find(m => m.id === botMessageId);
    if (!botMessage || botMessage.isUser) return false;
    
    // In viewer mode, don't show accordion if bot message is DISCARDED
    if (this.isViewerMode && botMessage.state === 'DISCARDED') {
      return false;
    }
    
    // Get the user message ID that this bot message answered
    const answeredMessageId = botMessage.answeredMessageId;
    if (!answeredMessageId) return false;
    
    // Check if ANY bot message (including MERGED) that answered the same user has traces
    // In viewer mode, exclude DISCARDED messages since their traces are shown in discarded-message-inline
    const allBotResponses = this._allMessages.filter(m => 
      !m.isUser && 
      m.answeredMessageId === answeredMessageId &&
      !(this.isViewerMode && m.state === 'DISCARDED')
    );
    
    return allBotResponses.some(response => !!response.agentTrace);
  }

  /**
   * Get traces for a specific bot message, including traces from MERGED messages
   */
  getBotTraces(botMessageId: string): any[] {
    if (!this._allMessages) return [];
    
    const botMessage = this._allMessages.find(m => m.id === botMessageId);
    if (!botMessage) return [];
    
    // Get the user message ID that this bot message answered
    const answeredMessageId = botMessage.answeredMessageId;
    if (!answeredMessageId) return [];
    
    // Find ALL bot messages (including MERGED) that answered the same user message
    // In viewer mode, exclude DISCARDED messages since their traces are shown in discarded-message-inline
    const allBotResponses = this._allMessages.filter(m => 
      !m.isUser && 
      m.answeredMessageId === answeredMessageId &&
      m.agentTrace &&
      !(this.isViewerMode && m.state === 'DISCARDED')
    );
    
    // Combine traces from ALL messages (PROCESSED + MERGED) for complete trace view.
    // Deduplicate by agentTrace content: in some API formats all MERGED messages carry
    // an identical copy of the trace (unlike version:4 where only the final message has it).
    const seenTraceKeys = new Set<string>();
    const allTraces: any[] = [];
    for (const response of allBotResponses) {
      const traceKey = typeof response.agentTrace === 'string'
        ? response.agentTrace
        : JSON.stringify(response.agentTrace);
      if (seenTraceKeys.has(traceKey)) continue;
      seenTraceKeys.add(traceKey);
      const traces = this.parseAndTransformAgentTrace(response);
      allTraces.push(...traces);
    }
    
    return allTraces;
  }

  private computeThinkingText(userMessageId: string): string {
    // PRIMERO: Si el bot ya envió la respuesta final, mostrar "Pensamiento" 
    const botResponse = this.findBotMessageForUser(userMessageId);
    if (botResponse) return 'Pensamiento';
    
    // SEGUNDO: Si este mensaje de usuario NO es el último mensaje de usuario, mostrar "Pensamiento"
    // (porque significa que el usuario ya envió otro mensaje después)
    if (!this.isLastUserMessage(userMessageId)) return 'Pensamiento';
    
    // TERCERO: Si no hay respuesta del bot, revisar eventos en tiempo real
    const events = this.getUserMessageEvents(userMessageId);
    
    // Si tampoco hay eventos, mostrar "Pensamiento"
    if (events.length === 0) return 'Pensamiento';

    const lastEvent = events.at(-1);
    
    if (lastEvent?.traceType === 'thinking') {
      return lastEvent.data?.text?.slice(0, 50) + '...';
    }
    if (lastEvent?.traceType === 'tool_use') {
      return `Usando herramienta ${lastEvent.data?.tool_name}`;
    }
    if (lastEvent?.traceType === 'tool_output') {
      return `Procesando respuesta de herramienta`;
    }
    
    return 'Pensando...';
  }

  getThinkingState(userMessageId: string): { text: string; opacity: number } {
    const newText = this.computeThinkingText(userMessageId);
    let state = this.thinkingState.get(userMessageId);

    if (!state) {
      // Primera vez - inicializar
      state = { 
        displayText: newText, 
        opacity: 1, 
        queue: [],
        isTransitioning: false,
        lastProcessedText: newText
      };
      this.thinkingState.set(userMessageId, state);
      return { text: state.displayText, opacity: state.opacity };
    }

    // Si el bot ya respondió, forzar "Pensamiento" inmediatamente sin transición
    const botResponse = this.findBotMessageForUser(userMessageId);
    if (botResponse) {
      state.displayText = 'Pensamiento';
      state.opacity = 1;
      state.queue = [];
      state.isTransitioning = false;
      state.lastProcessedText = 'Pensamiento';
      return { text: state.displayText, opacity: state.opacity };
    }

    // Si el texto nuevo es diferente al último procesado, agregarlo a la cola
    if (newText !== state.lastProcessedText && !state.queue.includes(newText)) {
      state.queue.push(newText);
      state.lastProcessedText = newText;
      
      // Iniciar procesamiento de cola si no está en transición
      if (!state.isTransitioning) {
        this.processQueue(userMessageId);
      }
    }

    return { text: state.displayText, opacity: state.opacity };
  }

  private processQueue(userMessageId: string): void {
    const state = this.thinkingState.get(userMessageId);
    if (!state || state.queue.length === 0) {
      if (state) state.isTransitioning = false;
      return;
    }

    state.isTransitioning = true;
    
    // Fade-out
    state.opacity = 0;
    
    setTimeout(() => {
      const currentState = this.thinkingState.get(userMessageId);
      if (!currentState) return;
      
      // Tomar siguiente texto de la cola
      const nextText = currentState.queue.shift();
      if (nextText) {
        currentState.displayText = nextText;
      }
      
      // Fade-in
      currentState.opacity = 1;
      
      // Esperar un momento antes de procesar el siguiente
      setTimeout(() => {
        this.processQueue(userMessageId);
      }, 600); // Tiempo mínimo de visualización
      
    }, 250); // Duración del fade-out
  }

  getLastThinkingText(userMessageId: string): string {
    return this.getThinkingState(userMessageId).text;
  }

  getThinkingText(userMessageId: string): string {
    return this.getThinkingState(userMessageId).text;
  }

  getThinkingOpacity(userMessageId: string): number {
    return this.getThinkingState(userMessageId).opacity;
  }

  isThinking(): boolean {
    return this.chatService.pendingProcessMessages().length > 0;
  }

  isFirstBotMessageAfterUser(currentIndex: number): boolean {
    if (!this.messages || currentIndex === 0) return false;

    // Current message must be from bot
    if (this.messages[currentIndex].isUser) return false;

    // Previous message must be from user
    return this.messages[currentIndex - 1]?.isUser ?? false;
  }

  hasProcessMessagesForPreviousUser(currentIndex: number): boolean {
    if (!this.messages || currentIndex === 0) return false;

    // Current message must be from bot
    if (this.messages[currentIndex].isUser) return false;

    // Previous message must be from user
    const previousMessage = this.messages[currentIndex - 1];
    if (!previousMessage?.isUser) return false;

    // Check if previous user message has process messages
    return this.getUserMessageEvents(previousMessage.id).length > 0;
  }

  isConsecutiveBotMessage(currentIndex: number): boolean {
    if (!this.messages || currentIndex === 0) return false;
    
    // Current message must be from bot
    if (this.messages[currentIndex].isUser) return false;

    // Previous message must also be from bot
    return !this.messages[currentIndex - 1]?.isUser;
  }

  /**
   * Check if this is the FIRST bot message after a user message
   */
  isFirstBotAfterUser(currentIndex: number): boolean {
    if (!this.messages || currentIndex === 0) return false;
    
    // Current message must be from bot
    const currentMessage = this.messages[currentIndex];
    if (currentMessage.isUser) return false;
    
    const previousMessage = this.messages[currentIndex - 1];
    
    // If previous is user, this is definitely first bot after user
    if (previousMessage.isUser) return true;
    
    // If previous is bot, check if they answered DIFFERENT user messages
    if (!previousMessage.isUser) {
      // If current bot answered a different user than previous bot, this is first in new sequence
      return currentMessage.answeredMessageId !== previousMessage.answeredMessageId;
    }
    
    return false;
  }

  /**
   * Check if this is the first VISIBLE bot message responding to a specific user message.
   * In viewer mode, when multiple bot messages have the same answeredMessageId (split responses),
   * only the first visible one should show the avatar.
   */
  isFirstVisibleBotForAnswer(messageId: string): boolean {
    if (!this._visibleMessages) return false;
    
    // Find the index of this message in the visible array
    const currentIndex = this._visibleMessages.findIndex(m => m.id === messageId);
    if (currentIndex === -1) return false;
    
    const currentMessage = this._visibleMessages[currentIndex];
    if (currentMessage.isUser) return false;
    
    const answeredMessageId = currentMessage.answeredMessageId;
    if (!answeredMessageId) return false;
    
    // Check if there's any previous VISIBLE bot message with the same answeredMessageId
    for (let i = 0; i < currentIndex; i++) {
      const prevMessage = this._visibleMessages[i];
      if (!prevMessage.isUser && prevMessage.answeredMessageId === answeredMessageId) {
        return false; // Found an earlier message with same answeredMessageId, so this is NOT first
      }
    }
    
    // No previous visible message with same answeredMessageId found, this is the first
    return true;
  }

  onButtonClick(value: string): void {
    this.sendMessage.emit(value);
  }

  /**
   * Gets the representative (first) discarded message for each group that should appear
   * before a given visible message. Groups are formed by answeredMessageId, so all
   * discarded messages answering the same user message are compacted into one panel —
   * similar to how MERGED messages work.
   */
  getDiscardedBefore(currentMessage: Message): Message[] {
    if (!this.isViewerMode || !this._allMessages?.length || !this.messages?.length) return [];
    
    const currentIndex = this.messages.indexOf(currentMessage);
    if (currentIndex < 0) return [];
    
    const currentTime = new Date(currentMessage.timestamp).getTime();
    const prevTime = currentIndex > 0 
      ? new Date(this.messages[currentIndex - 1].timestamp).getTime() 
      : 0;
    
    // Find discarded BOT messages between previous message and current message
    const discardedMessages = this._allMessages.filter(msg => msg.state === 'DISCARDED' && !msg.isUser);
    
    const inWindow = discardedMessages.filter(discarded => {
      const discardedTime = new Date(discarded.timestamp).getTime();
      return discardedTime > prevTime && discardedTime <= currentTime;
    });

    // Return only the FIRST message of each answeredMessageId group to compact traces
    const seenKeys = new Set<string>();
    return inWindow.filter(msg => {
      const key = msg.answeredMessageId || msg.id;
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });
  }

  /**
   * Returns all discarded BOT messages that belong to the same group as the
   * representative message (same answeredMessageId). Used to pass the full group
   * to DiscardedMessageInlineComponent so it can render all traces compacted.
   */
  getDiscardedGroupMessages(representative: Message): Message[] {
    if (!this._allMessages) return [representative];
    const key = representative.answeredMessageId || representative.id;
    return this._allMessages.filter(m =>
      m.state === 'DISCARDED' &&
      !m.isUser &&
      (m.answeredMessageId || m.id) === key
    );
  }

  /**
   * Check if agentName subtitle should be shown for this trace
   * Shows subtitle when agentName changes from previous trace
   */
  shouldShowAgentName(traces: any[], currentIndex: number): boolean {
    if (currentIndex === 0) return true; // Always show for first trace
    
    const currentAgentName = traces[currentIndex]?.agentName;
    const previousAgentName = traces[currentIndex - 1]?.agentName;
    
    return currentAgentName !== previousAgentName;
  }

  /**
   * Check if the previous user message has traces (for avatar logic in live chat)
   */
  hasPreviousUserTraces(currentIndex: number): boolean {
    if (!this.messages || currentIndex === 0) return false;
    
    // Current message must be from bot
    if (this.messages[currentIndex].isUser) return false;
    
    // Find the most recent user message before current bot message
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (this.messages[i].isUser) {
        // Found the most recent user message, check if it has traces
        return this.hasAnyTraces(this.messages[i].id);
      }
    }
    
    return false;
  }

  /**
   * Determines if a bot message should show an avatar (positioned absolutely).
   * The spacer is always present; this only controls avatar visibility.
   * 
   * Rules:
   * - If an accordion was shown (with its own avatar), don't show avatar on messages
   * - If no accordion exists, show avatar only on the first bot message of the group
   */
  shouldShowBotAvatar(botMessageId: string, currentIndex: number): boolean {
    if (!this.messages) return false;
    
    const currentMessage = this.messages[currentIndex];
    if (currentMessage.isUser) return false;
    
    const answeredMessageId = currentMessage.answeredMessageId;
    
    if (this.isViewerMode) {
      // In viewer mode:
      // Show avatar only if there's NO accordion AND this is the first visible bot for this answer
      const hasAccordion = this.hasBotTraces(botMessageId) && this.isFirstVisibleBotForAnswer(botMessageId);
      return !hasAccordion && this.isFirstVisibleBotForAnswer(botMessageId);
    } else {
      // In live mode:
      // If there's an accordion (with avatar), don't show avatar on any bot message
      if (answeredMessageId && this.hasAnyTraces(answeredMessageId)) {
        return false;
      }
      
      // If no accordion, show avatar only on first visible bot for this answer
      return this.isFirstVisibleBotForAnswer(botMessageId);
    }
  }

  /**
   * Get agentName from a trace object (handles both ProcessMessage and stored traces)
   */
  getAgentNameFromTrace(trace: any): string {
    return trace?.agentName || 'Agent';
  }

  /**
   * Check if agentName should be shown for a specific trace, verifying it exists first
   */
  shouldShowAgentNameForTrace(traces: any[], currentIndex: number): boolean {
    if (!traces[currentIndex]?.agentName) return false;
    return this.shouldShowAgentName(traces, currentIndex);
  }

  /**
   * Set view mode for a trace
   */
  setViewMode(trace: any, mode: 'clean' | 'raw'): void {
    const currentMap = new Map(this.traceViewModes());
    const traceKey = this.getTraceKey(trace);
    
    // If clicking the same mode, toggle it off (hide content)
    if (currentMap.get(traceKey) === mode) {
      currentMap.delete(traceKey);
    } else {
      currentMap.set(traceKey, mode);
    }
    
    this.traceViewModes.set(currentMap);
  }

  /**
   * Get view mode for a trace
   */
  getViewMode(trace: any): 'clean' | 'raw' | null {
    return this.traceViewModes().get(this.getTraceKey(trace)) || null;
  }

  /**
   * Generate a unique key for a trace
   */
  private getTraceKey(trace: any): string {
    return `${trace.id || trace.type}-${trace.timestamp}`;
  }
}
